import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { adminApi } from "../services/api";
import { useAuthStore } from "../stores/authStore";
import PermissionGate from "../components/PermissionGate";

type Tab = "completions" | "transactions";

export default function FamilyDetail() {
  const { familyId } = useParams<{ familyId: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuthStore();
  const [data,         setData]         = useState<{ family: any; kids: any[]; members: any[] } | null>(null);
  const [completions,  setCompletions]  = useState<any[]>([]);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [tab,          setTab]          = useState<Tab>("completions");
  const [loading,      setLoading]      = useState(true);
  const [pinModal,     setPinModal]     = useState<{ kidId: string; name: string } | null>(null);
  const [pinValue,     setPinValue]     = useState("");
  const [balModal,     setBalModal]     = useState<{ kidId: string; name: string; balance: number } | null>(null);
  const [balDelta,     setBalDelta]     = useState("");
  const [balReason,    setBalReason]    = useState("");
  const [photoUrl,     setPhotoUrl]     = useState<{ url: string } | null>(null);
  const [saving,       setSaving]       = useState(false);

  useEffect(() => {
    if (!familyId) return;
    setLoading(true);
    Promise.all([
      adminApi.getFamily(familyId).then(setData),
      adminApi.listCompletions(familyId).then((r) => setCompletions(r.completions)),
      adminApi.listTransactions(familyId).then((r) => setTransactions(r.transactions)),
    ]).finally(() => setLoading(false));
  }, [familyId]);

  async function exportBackup() {
    if (!familyId) return;
    const res = await adminApi.exportBackup(familyId);
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `backup_${familyId}_${new Date().toISOString().slice(0,10)}.json`;
    a.click();
  }

  async function deleteFamily() {
    if (!familyId || !data) return;
    if (!window.confirm(`Delete "${data.family.name}" permanently? This cannot be undone.`)) return;
    await adminApi.deleteFamily(familyId);
    navigate("/families");
  }

  async function savePin() {
    if (!familyId || !pinModal || pinValue.length < 5) return;
    setSaving(true);
    try { await adminApi.kidPinReset(familyId, pinModal.kidId, pinValue); setPinModal(null); setPinValue(""); }
    finally { setSaving(false); }
  }

  async function saveBalance() {
    if (!familyId || !balModal || !balDelta || !balReason) return;
    setSaving(true);
    try {
      await adminApi.kidBalance(familyId, balModal.kidId, parseInt(balDelta), balReason);
      const updated = await adminApi.getFamily(familyId);
      setData(updated);
      setBalModal(null); setBalDelta(""); setBalReason("");
    } finally { setSaving(false); }
  }

  async function viewPhoto(completionId: string) {
    if (!familyId) return;
    const { url } = await adminApi.getPhotoUrl(familyId, completionId);
    setPhotoUrl({ url });
  }

  if (loading || !data) return <div style={{ color: "var(--text-muted)" }}>Loading…</div>;

  const { family, kids } = data;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, maxWidth: 900 }}>
      <div style={{ fontSize: 12, color: "var(--text-muted)", display: "flex", gap: 6 }}>
        <span style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => navigate("/families")}>Families</span>
        <span>›</span>
        <span>{family.name}</span>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <h1 style={{ fontSize: 18, fontWeight: 900, flex: 1 }}>{family.name}</h1>
        <PermissionGate permission="backup:export">
          <button className="btn" onClick={exportBackup}>⬇ Export Backup</button>
        </PermissionGate>
        <PermissionGate permission="families:delete">
          <button className="btn btn-danger" onClick={deleteFamily}>🗑 Delete Family</button>
        </PermissionGate>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div className="card">
          <div style={{ fontWeight: 800, fontSize: 12, textTransform: "uppercase", letterSpacing: ".07em", color: "var(--text-muted)", marginBottom: 10 }}>Family Info</div>
          {[
            ["ID",          family.familyId],
            ["Owner email", family.parentEmail],
            ["Members",     data.members.length],
            ["Created",     family.createdAt?.slice(0,10)],
            ["Last active", family.lastActiveAt?.slice(0,10) ?? "—"],
            ["Point rate",  family.pointRate ? `$${family.pointRate} / ⭐` : "—"],
          ].map(([l, v]) => (
            <div key={String(l)} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderTop: "1px solid var(--border)", fontSize: 13 }}>
              <span style={{ color: "var(--text-muted)" }}>{l}</span>
              <span style={{ fontFamily: "monospace", fontSize: 12 }}>{String(v)}</span>
            </div>
          ))}
        </div>

        <div className="card">
          <div style={{ fontWeight: 800, fontSize: 12, textTransform: "uppercase", letterSpacing: ".07em", color: "var(--text-muted)", marginBottom: 10 }}>Kids</div>
          {kids.map((k) => (
            <div key={k.kidId} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderTop: "1px solid var(--border)" }}>
              <span style={{ fontSize: 20 }}>{k.avatar}</span>
              <span style={{ flex: 1, fontWeight: 600 }}>{k.name}</span>
              <span style={{ color: "#fcd34d", fontWeight: 700 }}>⭐ {k.pointBalance}</span>
              <PermissionGate permission="kids:pin_reset">
                <button className="btn" style={{ fontSize: 10, padding: "2px 7px" }} onClick={() => { setPinModal({ kidId: k.kidId, name: k.name }); setPinValue(""); }}>PIN</button>
              </PermissionGate>
              <PermissionGate permission="kids:balance">
                <button className="btn" style={{ fontSize: 10, padding: "2px 7px" }} onClick={() => { setBalModal({ kidId: k.kidId, name: k.name, balance: k.pointBalance }); setBalDelta(""); setBalReason(""); }}>+/−</button>
              </PermissionGate>
              <PermissionGate permission="kids:delete">
                <button className="btn btn-danger" style={{ fontSize: 10, padding: "2px 7px" }}
                  onClick={async () => {
                    if (window.confirm(`Delete ${k.name}?`)) {
                      await adminApi.deleteKid(familyId!, k.kidId);
                      setData(await adminApi.getFamily(familyId!));
                    }
                  }}>🗑</button>
              </PermissionGate>
            </div>
          ))}
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", borderBottom: "1px solid var(--border)" }}>
          {(["completions", "transactions"] as Tab[]).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding: "10px 16px", fontSize: 13, fontWeight: tab === t ? 700 : 400, color: tab === t ? "var(--accent)" : "var(--text-muted)", background: "none", border: "none", borderBottom: tab === t ? "2px solid var(--accent)" : "2px solid transparent" }}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
        <div style={{ padding: 14 }}>
          {tab === "completions" && completions.map((c) => (
            <div key={c.completionId} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderTop: "1px solid var(--border)", fontSize: 12 }}>
              <span style={{ color: "var(--text-muted)", fontFamily: "monospace", minWidth: 90 }}>{c.submittedAt?.slice(0,10)}</span>
              <span style={{ flex: 1 }}>{c.challengeId}</span>
              <span className={`badge ${c.status === "approved" ? "badge-l2" : c.status === "pending" ? "badge-admin" : "badge-root"}`}>{c.status}</span>
              {c.photoKey && <button className="btn" style={{ fontSize: 10, padding: "2px 6px" }} onClick={() => viewPhoto(c.completionId)}>📸 View</button>}
            </div>
          ))}
          {tab === "transactions" && transactions.map((t) => (
            <div key={t.txnId} style={{ display: "flex", gap: 10, padding: "7px 0", borderTop: "1px solid var(--border)", fontSize: 12 }}>
              <span style={{ color: "var(--text-muted)", fontFamily: "monospace", minWidth: 90 }}>{t.createdAt?.slice(0,10)}</span>
              <span style={{ flex: 1 }}>{t.reason}</span>
              <span style={{ color: t.amount > 0 ? "#86efac" : "#f87171", fontWeight: 700 }}>{t.amount > 0 ? "+" : ""}{t.amount} ⭐</span>
            </div>
          ))}
        </div>
      </div>

      {pinModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}
          onClick={(e) => e.target === e.currentTarget && setPinModal(null)}>
          <div className="card" style={{ width: 320, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontWeight: 800 }}>🔐 Reset PIN for {pinModal.name}</div>
            <input type="password" inputMode="numeric" maxLength={10} placeholder="New PIN (5+ digits)"
              value={pinValue} onChange={(e) => setPinValue(e.target.value.replace(/\D/g, ""))}
              style={{ padding: "10px 14px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface2)", color: "var(--text)", fontSize: 18, textAlign: "center", letterSpacing: "0.2em" }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} disabled={saving || pinValue.length < 5} onClick={savePin}>{saving ? "Saving…" : "Save PIN"}</button>
              <button className="btn" onClick={() => setPinModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {balModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}
          onClick={(e) => e.target === e.currentTarget && setBalModal(null)}>
          <div className="card" style={{ width: 340, display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontWeight: 800 }}>⭐ Adjust Balance for {balModal.name}</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Current balance: {balModal.balance} ⭐</div>
            <input type="number" placeholder="Delta (e.g. 50 or -20)" value={balDelta} onChange={(e) => setBalDelta(e.target.value)}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface2)", color: "var(--text)", fontSize: 14 }} />
            <input type="text" placeholder="Reason (required)" value={balReason} onChange={(e) => setBalReason(e.target.value)}
              style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid var(--border)", background: "var(--surface2)", color: "var(--text)", fontSize: 14 }} />
            <div style={{ display: "flex", gap: 8 }}>
              <button className="btn btn-primary" style={{ flex: 1 }} disabled={saving || !balDelta || !balReason} onClick={saveBalance}>{saving ? "Saving…" : "Apply"}</button>
              <button className="btn" onClick={() => setBalModal(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {photoUrl && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}
          onClick={() => setPhotoUrl(null)}>
          <img src={photoUrl.url} alt="Quest photo" style={{ maxWidth: "90vw", maxHeight: "90vh", borderRadius: 12 }} onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
